import type { OwaDate } from 'owa-datetime';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type FreeBusyType from '../schema/FreeBusyType';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import type WorkingHoursType from 'owa-service/lib/contract/WorkingHoursType';
import freeBusyCache from '../cacheV1/freeBusyCache';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getAttendeeDetailedEvents as getAttendeeDetailedEventsV2 } from './getAttendeeDetailedEvents';
import { getDetailedFreeBusy as getDetailedFreeBusyV2 } from './getDetailedFreeBusy';
import { getAggregatedFreeBusy as getAggregatedFreeBusyV2 } from './getAggregatedFreeBusy';
import { getAttendeeWorkingHours as getAttendeeWorkingHoursV2 } from './getAttendeeWorkingHours';
import type { GetScheduleFlexibleWorkingHoursView } from 'owa-graph-schema';
import { getAttendeeFlexibleWorkingHours as getAttendeeFlexibleWorkingHoursV2 } from './getAttendeeFlexibleWorkingHours';
import { getAttendeeTimeZone as getAttendeeTimeZoneV2 } from './getAttendeeTimeZone';
/**
 * Gets an array of free busy events for an attendee between the start and end time
 * converted to the given time zone
 * @param sessionId Id for this free/busy cache session, undefined is the default session
 * @param attendee The SMTP address for which free busy is requested
 * @param start The start time for which to retrieve free busy
 * @param end The end time for which to retrieve free busy
 * @param timezoneToSet An optional time zone for events to be converted to
 */
export function getAttendeeDetailedEvents(
    sessionId: string | undefined,
    attendee: string,
    start: OwaDate,
    end: OwaDate,
    timezoneToSet?: string
): CalendarEvent[] {
    if (sessionId && isFeatureEnabled('cal-freebusy-cache-v2')) {
        return getAttendeeDetailedEventsV2(sessionId, attendee, start, end, timezoneToSet);
    } else {
        return freeBusyCache.getAttendeeDetailedEvents(attendee, start, end, timezoneToSet);
    }
}

/**
 * Gets an array of free busy information for an attendee between the start and end time
 * @param sessionId Id for this free/busy cache session, undefined is the default session
 * @param attendee The SMTP address for which free busy is requested
 * @param start The start time for which to retrieve free busy
 * @param end The end time for which to retrieve free busy
 * @param filter An optional item to filter out of the free busy results
 */
export function getDetailedFreeBusy(
    sessionId: string | undefined,
    attendee: string,
    start: OwaDate,
    end: OwaDate,
    filter?: CalendarEvent
): FreeBusyType[] {
    if (sessionId && isFeatureEnabled('cal-freebusy-cache-v2')) {
        return getDetailedFreeBusyV2(sessionId, attendee, start, end);
    } else {
        return freeBusyCache.getDetailedFreeBusy(attendee, start, end, filter);
    }
}

/**
 * Gets the most severe free busy status between start and end time
 * @param sessionId Id for this free/busy cache session, undefined is the default session
 * @param attendee: The SMTP address for which Free Busy was requested
 * @param start: The start time of the event
 * @param end: The end time of the event
 * @param isAllDayEvent: true if the event spans the entire day
 * @param filter: An optional CalendarEvent to filter out of the results
 */
export function getAggregatedFreeBusy(
    sessionId: string | undefined,
    attendee: string,
    start: OwaDate,
    end: OwaDate,
    isAllDayEvent: boolean,
    filter?: CalendarEvent
): FreeBusyType {
    if (sessionId && isFeatureEnabled('cal-freebusy-cache-v2')) {
        return getAggregatedFreeBusyV2(sessionId, attendee, start, end, isAllDayEvent);
    } else {
        return freeBusyCache.getAggregatedFreeBusy(attendee, start, end, isAllDayEvent, filter);
    }
}

/**
 * Gets the WorkingHours for an attendee
 * @param attendee The attendee for which working hours are requested
 */
export function getAttendeeWorkingHours(
    sessionId: string | undefined,
    attendee: AttendeeType
): WorkingHoursType | undefined {
    if (sessionId && isFeatureEnabled('cal-freebusy-cache-v2')) {
        return getAttendeeWorkingHoursV2(sessionId, attendee);
    } else {
        return freeBusyCache.getAttendeeWorkingHours(attendee);
    }
}

/**
 * Gets the WorkingHours for an attendee
 * @param sessionId Id for this free/busy cache session, undefined is the default session
 * @param attendee The attendee for which working hours are requested
 */
export function getAttendeeFlexibleWorkingHours(
    sessionId: string | undefined,
    attendee: AttendeeType
): GetScheduleFlexibleWorkingHoursView[] {
    if (sessionId && isFeatureEnabled('cal-freebusy-cache-v2')) {
        return getAttendeeFlexibleWorkingHoursV2(sessionId, attendee);
    } else {
        return freeBusyCache.getAttendeeFlexibleWorkingHours(attendee);
    }
}

/**
 * Gets the TimeZone for an attendee.
 * @param sessionId Id for this free/busy cache session, undefined is the default session
 * @param attendee The attendee for which time zone is requested
 */
export function getAttendeeTimeZone(
    sessionId: string | undefined,
    attendee: AttendeeType,
    eventDate: OwaDate
): string | undefined {
    if (sessionId && isFeatureEnabled('cal-freebusy-cache-v2')) {
        return getAttendeeTimeZoneV2(sessionId, attendee, eventDate);
    } else {
        return freeBusyCache.getAttendeeTimeZone(attendee);
    }
}
